import React from 'react';

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Flex } from '@resideo/blueprint-react';

import ModalHeader from 'components/common/ModalHeader';
import Typography from 'components/common/Typography';

import {
  OrderedList,
  PreLineText,
  ScrollableArea,
} from '../../DataStreamScreen.style';

import { StepComponentProps, Steps } from 'types/types';

const components = {
  bold: <strong />,
  linkTo: <Link to='https://resideo.com/' target='_blank' />,
};

const { Title } = Typography;

const LegalTitle = styled(Title)`
  margin-bottom: 0;
`;

const LegalSection = ({ sectionKey = '' }: { sectionKey: string }) => {
  const { t } = useTranslation();

  return (
    <Flex flexDirection='column' paddingBottom='16px'>
      <LegalTitle level={3}>{t(`${sectionKey}.title`)}</LegalTitle>

      <PreLineText marginTop='0.5rem'>
        <Trans i18nKey={t(`${sectionKey}.content`)} components={components} />
      </PreLineText>
    </Flex>
  );
};

const LegalList = ({
  sectionKey = '',
  items,
  subtitle = false,
}: {
  sectionKey: string;
  items?: string[];
  subtitle?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Flex flexDirection='column' paddingBottom='16px'>
      <LegalTitle level={3}>{t(`${sectionKey}.title`)}</LegalTitle>

      {subtitle ? (
        <PreLineText marginTop='0.5rem'>
          {t(`${sectionKey}.subtitle`)}
        </PreLineText>
      ) : undefined}

      {items?.length && (
        <OrderedList type='a'>
          {items?.map(item => (
            <li key={item} style={{ textAlign: 'justify' }}>
              <Trans
                i18nKey={t(`${sectionKey}.content.${item}`)}
                components={components}
              />
            </li>
          ))}
        </OrderedList>
      )}
    </Flex>
  );
};

const TermsAcceptanceStep = ({ closeModal, setStep }: StepComponentProps) => {
  const { t } = useTranslation();

  const comps = {
    bold: <strong />,
    linkTo: <Link to='https://resideo.com/' target='_blank' />,
  };

  return (
    <>
      <ModalHeader
        title={t('privacy.dataStream.accountSignIn.terms.title')}
        closeModal={closeModal}
      />

      <ScrollableArea>
        <Flex justifyContent='center'>
          <Title data-test='data-stream-sign-in-terms-title' level={2}>
            {t('privacy.dataStream.dataAccessAgreement.title')}
          </Title>
        </Flex>

        <PreLineText strong='true'>
          {t('privacy.dataStream.dataAccessAgreement.lastUpdated')}
        </PreLineText>
        <PreLineText strong='true'>
          {t('privacy.dataStream.dataAccessAgreement.region')}
        </PreLineText>

        <PreLineText marginBottom='16px' marginTop='16px'>
          <Trans
            i18nKey={t('privacy.dataStream.dataAccessAgreement.introduction')}
            components={comps}
          />
        </PreLineText>

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.agreement'
          items={['a', 'b', 'c']}
        />

        <LegalSection sectionKey='privacy.dataStream.dataAccessAgreement.sections.parties' />

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.scope'
          items={['a', 'c']}
        />

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.dataAccessProcess'
          items={['a', 'b', 'c', 'd']}
        />

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.userWarranties'
          subtitle={true}
          items={['a', 'b', 'c', 'd']}
        />

        <LegalSection sectionKey='privacy.dataStream.dataAccessAgreement.sections.permittedUse' />

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.disclaimers'
          items={['a', 'b', 'c', 'd']}
        />

        <LegalSection sectionKey='privacy.dataStream.dataAccessAgreement.sections.termination' />

        <LegalList
          sectionKey='privacy.dataStream.dataAccessAgreement.sections.general'
          items={['a', 'b', 'c']}
        />
      </ScrollableArea>

      <Flex
        data-test='data-stream-sign-in-terms-actions'
        justifyContent='flex-end'
        paddingTop='large'>
        <Button onClick={() => setStep(Steps.DEVICE_SELECTION)}>
          {t('common.next')}
        </Button>
      </Flex>
    </>
  );
};

export default TermsAcceptanceStep;
